import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@material-ui/core";
import React, { useState } from "react";
import { convertBuilderAssetToLibraryItem } from "../../../entities/BuilderAsset";
import { convertFileAssetToLibraryItem } from "../../../entities/FileAsset";
import { convertFolderToLibraryItem } from "../../../entities/Folder";
import { LibraryItem } from "../../../entities/LibraryItem";
import { LibraryItemType } from "../../../entities/LibraryItemType";
import { convertLinkAssetToLibraryItem } from "../../../entities/LinkAsset";
import { convertSeriesToLibraryItem } from "../../../entities/SermonSeries";
import { BuilderAssetService } from "../../../services/BuilderAssetService";
import { FileAssetService } from "../../../services/FIleAssetService";
import { FolderService } from "../../../services/FolderService";
import { LinkAssetService } from "../../../services/LinkAssetService";
import { SermonSeriesService } from "../../../services/SermonSeriesService";
import { useLibrary, useLibraryAlert } from "../LibraryPage";

interface Props {
    focusedItem: LibraryItem;
    open: boolean;
    onClose: () => void;
}

export function DeleteAssetDialog(props:Props){
    const { focusedItem } = props;
    const [disabled, setDisabled] = useState(false);
    const libraryAlert = useLibraryAlert();
    const context = useLibrary();


    const handleDelete = () => {
        switch(focusedItem.type) {
			case LibraryItemType.Folder:
			case LibraryItemType.PremiumSermon:
			case LibraryItemType.PremiumSermonSeries:
				return handleFolderDelete();
			case LibraryItemType.SermonSeries:
				return handleSeriesDelete();
			case LibraryItemType.BuilderAsset:
				return handleAssetDelete();
			case LibraryItemType.FileAsset:
				return handleFileDelete();
			case LibraryItemType.LinkAsset:
				return handleLinkDelete();
			default:
				return;
		}
    }

    const handleFolderDelete = async() => {
        setDisabled(true)
        const response = await FolderService.deleteFolder(focusedItem.id)
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            context.removeLibraryItem(focusedItem.id)
            libraryAlert(`${focusedItem.name} deleted`, async () => {
                const result = await FolderService.undeleteFolder(focusedItem.id);
                if(result.success) {
                    context.addLibraryItem(convertFolderToLibraryItem(result.data))
                    libraryAlert(`${focusedItem.name} restored`);
                } else {
                    libraryAlert(`Failed to restore ${focusedItem.name}`);
                }
            });
            props.onClose();
        }
    }

    const handleSeriesDelete = async() => {
        setDisabled(true)
        const response = await SermonSeriesService.deleteSermonSeries(focusedItem.id)
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            context.removeLibraryItem(focusedItem.id)
            libraryAlert(`${focusedItem.name} deleted`, async () => {
                const result = await SermonSeriesService.undeleteSermonSeries(focusedItem.id);
                if(result.success) {
                    context.addLibraryItem(convertSeriesToLibraryItem(result.data))
                    libraryAlert(`${focusedItem.name} restored`);
                } else {
                    libraryAlert(`Failed to restore ${focusedItem.name}`);
                }
            });
            props.onClose();
        }
    }

    const handleAssetDelete = async() => {
        setDisabled(true);
        const response = await BuilderAssetService.remove(focusedItem.id);
        setDisabled(false);

        if(response.success){
            context.removeLibraryItem(focusedItem.id);
            libraryAlert(`${focusedItem.name} deleted`, async () => {
                const result = await BuilderAssetService.undeleteBuilderAsset(focusedItem.id);
                if(result.success) {
                    context.addLibraryItem(convertBuilderAssetToLibraryItem(result.data));
                    libraryAlert(`${focusedItem.name} restored`);
                } else {
                    libraryAlert(`Failed to restore ${focusedItem.name}`);
                }
            });
            props.onClose();
        }
    }

    const handleFileDelete = async() => {
        setDisabled(true)
        const response = await FileAssetService.remove(focusedItem.id)
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            context.removeLibraryItem(focusedItem.id)
            libraryAlert(`${focusedItem.name} deleted`, async () => {
                const result = await FileAssetService.undeleteFileAsset(focusedItem.id);
                if(result.success) {
                    libraryAlert(`${focusedItem.name} restored`);
                    context.addLibraryItem(convertFileAssetToLibraryItem(result.data))
                } else {
                    libraryAlert(`Failed to restore ${focusedItem.name}`);
                }
            });
            props.onClose();
        }
    }

    const handleLinkDelete = async() => {
        setDisabled(true)
        const response = await LinkAssetService.delete(focusedItem.id)
        setDisabled(false)

        if(response.error) return libraryAlert(response.message);
        if(response.success){
            context.removeLibraryItem(focusedItem.id)
            libraryAlert(`${focusedItem.name} deleted`, async () => {
                const result = await LinkAssetService.undelete(focusedItem.id);
                if(result.success) {
                    libraryAlert(`${focusedItem.name} restored`);
                    context.addLibraryItem(convertLinkAssetToLibraryItem(result.data))
                } else {
                    libraryAlert(`Failed to restore ${focusedItem.name}`);
                }
            });
            props.onClose();
        }
    }

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            disableBackdropClick={disabled}
            disableEscapeKeyDown={disabled}
            aria-labelledby="delete-form-dialog-title"
        >
            <DialogTitle id="delete-form-dialog-title">Delete</DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    Are you sure you wish to continue?
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose} disabled={disabled} color="primary">
                    Cancel
                </Button>
                <Button disabled={disabled} onClick={() => handleDelete()} color="secondary">
                    Delete
            </Button>
            </DialogActions>
        </Dialog>
    );
}